import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Menu, MenuItem,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Button, CircularProgress, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Divider, FormControl, InputLabel,
  Select, useTheme, alpha, Chip, Alert
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';

import { useAuth } from '../contexts/AuthContext';
import { getProjects, getProjectUsers } from '../utils/api';
import { updateDocumentProject } from '../utils/documentProjectService';

/**
 * Component for managing document project access
 * @param {Object} props
 * @param {Object} props.document - Current document
 * @param {Function} props.onProjectChange - Callback when project changes
 */
const DocumentProjectAccess = ({ document, onProjectChange }) => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');

  // Fetch available projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await getProjects();
        if (response.success) {
          setProjects(response.projects || []);
          
          // Set the selected project based on the document's project_id
          if (document && document.project_id) {
            const projectMatch = response.projects.find(
              p => p.id.toString() === document.project_id.toString()
            );
            if (projectMatch) {
              setSelectedProject(projectMatch);
            }
          }
        } else {
          setError('Failed to load projects');
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [document]);
  
  // Fetch project users when a project is selected
  useEffect(() => {
    const fetchProjectUsers = async () => {
      if (!selectedProject) return;
      
      try {
        // Check if the getProjectUsers function exists
        if (typeof getProjectUsers !== 'function') {
          console.error('getProjectUsers function is not available');
          setProjectUsers([]);
          return;
        }
        
        setLoading(true); // Start loading state
        console.log(`Fetching users for project ${selectedProject.id}`);
        
        const response = await getProjectUsers(selectedProject.id);
        if (response && response.success) {
          console.log(`Got ${response.users?.length || 0} users for project ${selectedProject.id}`);
          setProjectUsers(response.users || []);
          // Clear any previous errors
          setError('');
        } else {
          // Handle error response
          console.error('Failed to get project users:', response?.message);
          setProjectUsers([]);
          setError(response?.message || 'Failed to load project users');
        }
      } catch (error) {
        console.error('Error fetching project users:', error);
        setProjectUsers([]);
        setError('Failed to load project users: ' + (error.message || 'Unknown error'));
      } finally {
        setLoading(false); // End loading state
      }
    };
    
    fetchProjectUsers();
  }, [selectedProject]);
  
  // Open the change project dialog
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };
  
  // Close the change project dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // Change the document's project
  const handleChangeProject = async (projectId) => {
    if (!document || !document.id) {
      setError('Document information is missing');
      return;
    }
    
    try {
      setLoading(true);
      const result = await updateDocumentProject(document.id, projectId);
      
      if (result.success) {
        // Update local state
        const newProject = projects.find(p => p.id.toString() === projectId.toString());
        setSelectedProject(newProject);
        
        // Call the parent callback
        if (onProjectChange) {
          onProjectChange(result.document);
        }
        
        setDialogOpen(false);
      } else {
        setError(result.error || 'Failed to update document project');
      }
    } catch (error) {
      console.error('Error updating document project:', error);
      setError('Failed to update document project');
    } finally {
      setLoading(false);
    }
  };
  
  // Determine if user can edit the project
  const canEditProject = () => {
    return currentUser && (
      currentUser.role === 'admin' ||
      (document && document.user_id === currentUser.id)
    );
  };
  
  // Render the component
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
          <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} /> Project Access
        </Typography>
        
        {canEditProject() && (
          <IconButton 
            size="small" 
            onClick={handleOpenDialog}
            sx={{ 
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {loading ? (
          <CircularProgress size={20} sx={{ mr: 1 }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              icon={<BusinessIcon />}
              label={selectedProject ? selectedProject.name : document?.project_name || 'Unknown Project'}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            />
            
            <Tooltip title="This document is accessible to all users assigned to this project">
              <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.text.secondary }} />
            </Tooltip>
          </Box>
        )}
      </Box>
      
      {/* Change Project Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change Project Access
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Changing the project will affect who can access this document. Only users assigned to the selected project will have access.
              </Alert>
              
              <Typography variant="subtitle2" gutterBottom>
                Select Project:
              </Typography>
              
              <List sx={{ 
                maxHeight: 300, 
                overflow: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                {projects.map((project) => (
                  <React.Fragment key={project.id}>
                    <ListItem
                      button
                      selected={selectedProject && selectedProject.id === project.id}
                      onClick={() => handleChangeProject(project.id)}
                      sx={{
                        borderLeft: selectedProject && selectedProject.id === project.id 
                          ? `4px solid ${theme.palette.primary.main}`
                          : '4px solid transparent',
                        pl: selectedProject && selectedProject.id === project.id ? 2 : 3
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          <BusinessIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={project.name} 
                        secondary={project.description || 'No description available'} 
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
                
                {projects.length === 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="No projects available" 
                      secondary="You need to create or be assigned to a project first" 
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Add Tooltip component to avoid reference error
const Tooltip = ({ children, title }) => {
  return (
    <div title={title} style={{ display: 'inline-flex' }}>
      {children}
    </div>
  );
};

export default DocumentProjectAccess;